import { useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useState } from 'react';

/**
 * 24 liste sayfasında birebir kopyalanan sil-mutasyonu bloğunu (useMutation + snackbar
 * başarı/hata + invalidateQueries + setDeleting(null)) tek yerde toplar. Dönen
 * `confirmDialogProps` doğrudan `<ConfirmDialog {...props} />`'e yayılır.
 */
export function useDeleteResource<TArg = number>(opts: {
  mutationFn: (arg: TArg) => Promise<unknown>;
  invalidateKeys: QueryKey[];
  successMessage: string;
  errorMessage?: string;
}) {
  const { mutationFn, invalidateKeys, successMessage, errorMessage = 'İşlem başarısız.' } = opts;
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [target, setTarget] = useState<TArg | null>(null);

  const mutation = useMutation({
    mutationFn,
    onSuccess: () => {
      enqueueSnackbar(successMessage, { variant: 'success' });
      invalidateKeys.forEach((queryKey) => queryClient.invalidateQueries({ queryKey }));
      setTarget(null);
    },
    onError: (err) =>
      enqueueSnackbar(err instanceof Error ? err.message : errorMessage, { variant: 'error' }),
  });

  return {
    /** Silinmek üzere seçili öğe (yoksa `null`). */
    target,
    /** Onay diyaloğunu bir öğe için aç. */
    request: (t: TArg) => setTarget(t),
    /** Onay diyaloğunu kapat. */
    cancel: () => setTarget(null),
    isPending: mutation.isPending,
    /** `<ConfirmDialog {...confirmDialogProps} title=… description=… />` */
    confirmDialogProps: {
      open: target !== null,
      loading: mutation.isPending,
      onConfirm: () => target !== null && mutation.mutate(target),
      onCancel: () => setTarget(null),
    },
  };
}
